import { useState } from 'react'
import {
  Card, Row, Col, Form, DatePicker, Button, Statistic, Typography,
  Progress, Table, Tag, Divider, Space, Alert,
} from 'antd'
import {
  DownloadOutlined, DashboardOutlined, ThunderboltOutlined,
  ExperimentOutlined, CarOutlined, WarningOutlined, CheckCircleOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { reportsApi } from '@/features/reports/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

export default function KPIDashboardPage() {
  const { plantId } = usePlantId()

  const [form] = Form.useForm()
  const [params, setParams] = useState({
    plant_id: plantId,
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().format('YYYY-MM-DD'),
  })

  const { data: kpi, isLoading } = useQuery({
    queryKey: ['kpi-dashboard', params],
    queryFn: () => reportsApi.kpiDashboard(params),
    enabled: !!params.plant_id,
    refetchInterval: 5 * 60 * 1000, // auto-refresh every 5 minutes
  })

  const handleSearch = () => {
    const vals = form.getFieldsValue()
    const [df, dt] = vals.dateRange || []
    setParams({
      plant_id: plantId,
      date_from: df ? df.format('YYYY-MM-DD') : params.date_from,
      date_to: dt ? dt.format('YYYY-MM-DD') : params.date_to,
    })
  }

  const prod = kpi?.production
  const qual = kpi?.quality
  const gate = kpi?.gate
  const capa = kpi?.capa

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            <DashboardOutlined style={{ marginRight: 8, color: '#1677ff' }} />
            Daily Mill KPI Dashboard
          </Title>
          <Text type="secondary">Real-time plant performance overview</Text>
        </Col>
        <Col>
          <Space>
            <Form form={form} layout="inline">
              <Form.Item name="dateRange">
                <RangePicker
                  defaultValue={[dayjs().startOf('month'), dayjs()]}
                  format="DD-MMM-YYYY"
                  size="small"
                />
              </Form.Item>
              <Form.Item>
                <Button size="small" type="primary" onClick={handleSearch}>Refresh</Button>
              </Form.Item>
            </Form>
            <Button
              icon={<DownloadOutlined />}
              href={reportsApi.fullPlantReportExportUrl(params)}
              download
            >
              Full Plant Report
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Production KPIs */}
      <Card
        title={<><ThunderboltOutlined style={{ color: '#faad14', marginRight: 8 }} />Production KPIs</>}
        style={{ marginBottom: 16 }}
        loading={isLoading}
      >
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Statistic
              title="Planned Output (kg)"
              value={prod?.total_planned_kg ?? 0}
              precision={3}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Actual Output (kg)"
              value={prod?.total_actual_kg ?? 0}
              precision={3}
            />
          </Col>
          <Col span={4}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Plan Achievement</Text>
              <br />
              <Text style={{
                fontSize: 24, fontWeight: 600,
                color: (prod?.plan_achievement_pct ?? 0) >= 95 ? '#389e0d' : '#d46b08'
              }}>
                {prod?.plan_achievement_pct ?? 0}%
              </Text>
              <Progress
                percent={Math.min(100, prod?.plan_achievement_pct ?? 0)}
                showInfo={false}
                strokeColor={(prod?.plan_achievement_pct ?? 0) >= 95 ? '#52c41a' : '#faad14'}
                size="small"
              />
            </div>
          </Col>
          <Col span={4}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Spindle Utilization</Text>
              <br />
              <Text style={{ fontSize: 24, fontWeight: 600 }}>{prod?.avg_spindle_utilization ?? 0}%</Text>
              <Progress
                percent={prod?.avg_spindle_utilization ?? 0}
                showInfo={false}
                strokeColor="#1677ff"
                size="small"
              />
            </div>
          </Col>
          <Col span={4}>
            <Statistic
              title="Avg Waste %"
              value={prod?.avg_waste_pct ?? 0}
              precision={4}
              suffix="%"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Power/kg (kWh)"
              value={prod?.avg_power_per_kg ?? 0}
              precision={4}
            />
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={16}>
          <Col span={4}>
            <Statistic
              title="Total Stoppage (min)"
              value={prod?.total_stoppage_minutes ?? 0}
              valueStyle={{ color: (prod?.total_stoppage_minutes ?? 0) > 120 ? '#cf1322' : '#d46b08' }}
            />
          </Col>
          <Col span={4}>
            <Statistic title="Total Power (kWh)" value={prod?.total_power_kwh ?? 0} precision={3} />
          </Col>
          <Col span={4}>
            <Statistic
              title="Quality Rejections (kg)"
              value={prod?.total_quality_rej_kg ?? 0}
              precision={3}
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Machine Efficiency %"
              value={prod?.avg_machine_efficiency ?? 0}
              precision={2}
              suffix="%"
            />
          </Col>
        </Row>
      </Card>

      {/* Quality KPIs */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card
            title={<><ExperimentOutlined style={{ color: '#52c41a', marginRight: 8 }} />Quality KPIs</>}
            loading={isLoading}
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic title="Total Lab Tests" value={qual?.total_tests ?? 0} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Pass"
                  value={qual?.total_pass ?? 0}
                  valueStyle={{ color: '#389e0d' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Quality Pass Rate</Text>
                  <br />
                  <Text style={{
                    fontSize: 24, fontWeight: 600,
                    color: (qual?.quality_pass_pct ?? 0) >= 95 ? '#389e0d' : '#d46b08'
                  }}>
                    {qual?.quality_pass_pct ?? 0}%
                  </Text>
                  <Progress
                    percent={qual?.quality_pass_pct ?? 0}
                    showInfo={false}
                    strokeColor={(qual?.quality_pass_pct ?? 0) >= 95 ? '#52c41a' : '#faad14'}
                    size="small"
                  />
                </div>
              </Col>
            </Row>

            {qual?.by_count && qual.by_count.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Table
                  dataSource={qual.by_count}
                  columns={[
                    { title: 'Count (Ne)', dataIndex: 'count_ne', key: 'count' },
                    { title: 'Tests', dataIndex: 'total_tests', key: 'total', align: 'right' as const },
                    {
                      title: 'Pass %', dataIndex: 'pass_pct', key: 'pct', align: 'right' as const,
                      render: (v: string) => {
                        const n = parseFloat(v)
                        return <Tag color={n >= 95 ? 'green' : n >= 85 ? 'orange' : 'red'}>{v}%</Tag>
                      }
                    },
                    { title: 'Avg Strength', dataIndex: 'avg_strength_rkm', key: 'str', align: 'right' as const },
                  ]}
                  rowKey="count_ne"
                  size="small"
                  pagination={false}
                />
              </>
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={<><WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />CAPA Status</>}
            loading={isLoading}
          >
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Statistic title="Total CAPAs" value={capa?.total ?? 0} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Open"
                  value={capa?.open ?? 0}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Overdue"
                  value={capa?.overdue ?? 0}
                  valueStyle={{ color: '#d46b08' }}
                  prefix={<WarningOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic title="In Progress" value={capa?.in_progress ?? 0} valueStyle={{ color: '#1677ff' }} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Closed"
                  value={capa?.closed ?? 0}
                  valueStyle={{ color: '#389e0d' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
            </Row>

            {capa?.overdue > 0 && (
              <Alert
                type="warning"
                message={`${capa.overdue} CAPA(s) are overdue — immediate action required`}
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Gate KPIs */}
      <Card
        title={<><CarOutlined style={{ color: '#1677ff', marginRight: 8 }} />Gate / Inward KPIs</>}
        loading={isLoading}
      >
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Statistic title="Total Entries" value={gate?.total_entries ?? 0} />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Invoice Wt (kg)"
              value={gate?.total_invoice_weight_kg ?? 0}
              precision={3}
            />
          </Col>
          {gate?.by_status && Object.entries(gate.by_status as Record<string, number>).map(([status, count]) => (
            <Col span={3} key={status}>
              <Statistic
                title={status.replace('_', ' ')}
                value={count as number}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
          ))}
        </Row>
      </Card>

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          href={reportsApi.fullPlantReportExportUrl(params)}
          download
        >
          Download Full Plant Report (All Sheets)
        </Button>
      </div>
    </div>
  )
}
